export interface FieldVisibilityState {
  isVisible: boolean;
  isEditable: boolean;
  isCalculated: boolean;
  visibilityReason?: string;
  fieldSource: "Benny/Human" | "Benny/Calculated" | "Human Only";
}

export interface InsuranceVerificationFormData {
  termination_date?: Date | null;
  appointment_datetime?: Date | null;
  effective_date?: Date | null;
  has_orthodontic_coverage?: boolean | null;
  ortho_lifetime_max_benefit?: number | null;
  ortho_amount_used?: number | null;
  ortho_amount_remaining?: number | null;
  ortho_percentage_plan_pays?: number | null;
  ortho_age_limit?: number | null;
  ortho_manual_or_automatic_payments?: string | null;
  ortho_payment_frequency?: string | null;
  ortho_initial_payment_percentage?: number | null;
  has_ortho_deductible_plan?: boolean | null;
  ortho_deductible_remaining?: number | null;
  has_ortho_waiting_period?: boolean | null;
  ortho_waiting_period_date?: Date | null;
  coordination_of_benefits?: string | null;
  notes?: string | null;
  pre_authorization_required?: string | null;
  group_accepts_assignment_of_benefits?: string | null;
  call_reference_number?: string | null;
  verification_method?: string | null;
}

// biome-ignore lint/complexity/noStaticOnlyClass: used as a namespace across callers; converting changes every call site
export class ConditionalDisplayManager {
  private static readonly FIELD_DEFINITIONS: Record<string, string> = {
    termination_date: "Benny/Human",
    effective_date: "Benny/Human",
    has_orthodontic_coverage: "Benny/Human",
    ortho_lifetime_max_benefit: "Benny/Human",
    ortho_amount_used: "Benny/Human",
    ortho_amount_remaining: "Benny/Calculated",
    ortho_percentage_plan_pays: "Benny/Human",
    ortho_age_limit: "Benny/Human",
    ortho_manual_or_automatic_payments: "Benny/Human",
    ortho_payment_frequency: "Benny/Human",
    ortho_initial_payment_percentage: "Benny/Human",
    has_ortho_deductible_plan: "Benny/Human",
    ortho_deductible_remaining: "Benny/Human",
    has_ortho_waiting_period: "Benny/Human",
    ortho_waiting_period_date: "Benny/Human",
    coordination_of_benefits: "Benny/Human",
    notes: "Human Only",
    pre_authorization_required: "Benny/Human",
    group_accepts_assignment_of_benefits: "Benny/Human",
    call_reference_number: "Benny/Human",
    verification_method: "Human Only",
  };

  private static readonly ORTHO_DETAIL_FIELDS = [
    "ortho_lifetime_max_benefit",
    "ortho_amount_used",
    "ortho_amount_remaining",
    "ortho_percentage_plan_pays",
    "ortho_age_limit",
    "ortho_manual_or_automatic_payments",
    "ortho_payment_frequency",
    "ortho_initial_payment_percentage",
    "has_ortho_deductible_plan",
    "ortho_deductible_remaining",
    "has_ortho_waiting_period",
    "ortho_waiting_period_date",
    "coordination_of_benefits",
    "group_accepts_assignment_of_benefits",
  ];

  private static readonly TERMINATION_DEPENDENT_FIELDS = [
    "effective_date",
    "has_orthodontic_coverage",
    ...this.ORTHO_DETAIL_FIELDS,
  ];

  private static readonly ORTHO_COVERAGE_DEPENDENT_FIELDS = [
    "pre_authorization_required",
    ...this.ORTHO_DETAIL_FIELDS,
  ];

  private static readonly PRE_AUTH_DEPENDENT_FIELDS = [
    ...this.ORTHO_DETAIL_FIELDS,
  ];

  // Coverage is only actually terminated if the termination date is on or before the
  // appointment date. If the appointment date is unknown, fall back to the
  // conservative default of treating the policy as terminated.
  static isCoverageTerminated(
    appointmentDatetime?: Date,
    terminationDatetime?: Date,
  ): boolean {
    if (!terminationDatetime || Number.isNaN(terminationDatetime.getTime())) {
      return false;
    }
    if (!appointmentDatetime || Number.isNaN(appointmentDatetime.getTime())) {
      return true;
    }
    return terminationDatetime.getTime() <= appointmentDatetime.getTime();
  }

  static shouldFieldBeEnabled(
    fieldName: string,
    formData: InsuranceVerificationFormData,
  ): boolean {
    const hasOrthodonticCoverage = formData.has_orthodontic_coverage;
    const hasOrthoDeductiblePlan = formData.has_ortho_deductible_plan;
    const hasOrthoWaitingPeriod = formData.has_ortho_waiting_period;
    const preAuthRequired = formData.pre_authorization_required;

    if (
      ConditionalDisplayManager.TERMINATION_DEPENDENT_FIELDS.includes(
        fieldName,
      ) &&
      ConditionalDisplayManager.isCoverageTerminated(
        formData.appointment_datetime ?? undefined,
        formData.termination_date ?? undefined,
      )
    ) {
      return false;
    }

    if (
      ConditionalDisplayManager.ORTHO_COVERAGE_DEPENDENT_FIELDS.includes(
        fieldName,
      ) &&
      hasOrthodonticCoverage !== true
    ) {
      return false;
    }

    if (
      ConditionalDisplayManager.PRE_AUTH_DEPENDENT_FIELDS.includes(fieldName) &&
      preAuthRequired === "YES"
    ) {
      return false;
    }

    if (
      fieldName === "ortho_deductible_remaining" &&
      hasOrthoDeductiblePlan === false
    ) {
      return true;
    }

    if (
      fieldName === "ortho_waiting_period_date" &&
      hasOrthoWaitingPeriod === false
    ) {
      return true;
    }

    return true;
  }

  static getFieldVisibilityReason(
    fieldName: string,
    formData: InsuranceVerificationFormData,
  ): string | undefined {
    const hasOrthodonticCoverage = formData.has_orthodontic_coverage;
    const hasOrthoDeductiblePlan = formData.has_ortho_deductible_plan;
    const hasOrthoWaitingPeriod = formData.has_ortho_waiting_period;
    const preAuthRequired = formData.pre_authorization_required;

    if (!ConditionalDisplayManager.shouldFieldBeEnabled(fieldName, formData)) {
      if (
        ConditionalDisplayManager.isCoverageTerminated(
          formData.appointment_datetime ?? undefined,
          formData.termination_date ?? undefined,
        )
      ) {
        return "This field is disabled because coverage has terminated";
      }
      if (hasOrthodonticCoverage !== true) {
        return "This field is disabled because orthodontic coverage has not been confirmed";
      }
      if (
        ConditionalDisplayManager.PRE_AUTH_DEPENDENT_FIELDS.includes(
          fieldName,
        ) &&
        preAuthRequired === "YES"
      ) {
        return "This field is disabled because pre-authorization is required";
      }
      if (
        fieldName === "ortho_deductible_remaining" &&
        hasOrthoDeductiblePlan === false
      ) {
        return "This field is disabled because there is no ortho deductible plan";
      }
      if (
        fieldName === "ortho_waiting_period_date" &&
        hasOrthoWaitingPeriod === false
      ) {
        return "This field is disabled because there is no ortho waiting period";
      }
    }

    return undefined;
  }

  static getFieldVisibilityState(
    fieldName: string,
    formData: InsuranceVerificationFormData,
  ): FieldVisibilityState {
    const isVisible = ConditionalDisplayManager.shouldFieldBeEnabled(
      fieldName,
      formData,
    );
    const fieldSource = ConditionalDisplayManager.FIELD_DEFINITIONS[
      fieldName
    ] as "Benny/Human" | "Benny/Calculated" | "Human Only";
    const isCalculated = fieldSource === "Benny/Calculated";
    let isEditable = isVisible && !isCalculated;

    if (
      fieldName === "ortho_deductible_remaining" &&
      formData.has_ortho_deductible_plan === false
    ) {
      isEditable = false;
    }

    if (
      fieldName === "ortho_waiting_period_date" &&
      formData.has_ortho_waiting_period === false
    ) {
      isEditable = false;
    }
    const visibilityReason = ConditionalDisplayManager.getFieldVisibilityReason(
      fieldName,
      formData,
    );

    return {
      isVisible,
      isEditable,
      isCalculated,
      visibilityReason,
      fieldSource,
    };
  }

  static getAllFieldsVisibility(
    formData: InsuranceVerificationFormData,
  ): Record<string, FieldVisibilityState> {
    const result: Record<string, FieldVisibilityState> = {};

    Object.keys(ConditionalDisplayManager.FIELD_DEFINITIONS).forEach(
      (fieldName) => {
        result[fieldName] = ConditionalDisplayManager.getFieldVisibilityState(
          fieldName,
          formData,
        );
      },
    );

    return result;
  }

  static getDisabledFields(formData: InsuranceVerificationFormData): string[] {
    return Object.keys(ConditionalDisplayManager.FIELD_DEFINITIONS).filter(
      (fieldName) =>
        !ConditionalDisplayManager.getFieldVisibilityState(fieldName, formData)
          .isEditable,
    );
  }

  static getCalculatedFields(): string[] {
    return Object.keys(ConditionalDisplayManager.FIELD_DEFINITIONS).filter(
      (fieldName) =>
        ConditionalDisplayManager.FIELD_DEFINITIONS[fieldName] ===
        "Benny/Calculated",
    );
  }
}
